'use client'

import Link from 'next/link'
import Image from 'next/image'
import { getDirectusImageUrl } from '@/lib/directus'
import { analytics, trackEvent } from '@/lib/analytics'
import { useImpressionEvent } from '@/hooks/useImpressionEvent'

export interface RailRelatedPost {
  slug: string
  title: string
  image?: string | null
}

interface RailKeepReadingProps {
  posts: RailRelatedPost[]
}

/** Shared by the impression and the click so CTR joins on one key. */
export const RAIL_KEEP_READING_LOCATION = 'blog_keep_reading_rail'

/**
 * Sidebar recirculation list (blog rail v2, Variant C). Fed by the same
 * relevance ranking as the footer's Related Articles — the page fetches one
 * ranked list and the rail takes the next slots after the footer's three, so
 * the two surfaces never repeat a post. Recirculation is the pattern every
 * measured top ad-supported blog runs in this position; pages/session is
 * now also ad revenue.
 */
export function RailKeepReading({ posts }: RailKeepReadingProps) {
  // Denominator. Without it the rail's clicks have no rate, so a rail nobody
  // scrolls to and a rail nobody wants read identically.
  const impressionRef = useImpressionEvent<HTMLDivElement>(
    RAIL_KEEP_READING_LOCATION,
    { surface: 'rail_keep_reading', count: posts.length },
    { emit: analytics.ctaSurfaceShownWhenVisible },
  )

  if (posts.length === 0) return null

  return (
    <div
      ref={impressionRef}
      className="rounded-[18px] border border-white/55 bg-white/55 p-4 backdrop-blur-md"
    >
      <h2 className="font-editorial text-[11px] font-bold uppercase tracking-wide text-green-700 mb-1.5">
        Keep reading
      </h2>
      <ul className="divide-y divide-white/60">
        {posts.map((post, index) => (
          <li key={post.slug}>
            <Link
              href={`/blog/${post.slug}`}
              prefetch={false}
              onClick={() =>
                trackEvent('cta_clicked', {
                  cta_location: RAIL_KEEP_READING_LOCATION,
                  cta_text: post.title,
                  target_slug: post.slug,
                  position: index + 1,
                })
              }
              className="group flex items-center gap-3 py-2.5"
            >
              <Image
                src={getDirectusImageUrl(post.image, 96, 72)}
                alt=""
                width={48}
                height={36}
                className="h-9 w-12 flex-shrink-0 rounded-lg object-cover"
              />
              <span className="text-[13px] font-semibold leading-snug text-gray-900 group-hover:text-green-700">
                {post.title}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
